// ABOUTME: Square icon badge representing a ticket's priority (low/medium/high/urgent).
// ABOUTME: Pass `ticketId` to make it a self-contained picker (opens dropdown, fires update mutation).

import { Box, ButtonBase, useTheme } from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { useTranslation } from "react-i18next";

import { DropdownBaseV2 } from "../../../../../components/theme/dropdowns/Dropdown";
import Text from "../../../../../components/theme/text/Text";
import { tokens } from "../../../../../theme";
import { support_ticket_priority } from "../../../../../utils/staticEnums";
import { getSupportTicketPriorityData } from "../../../../../utils/labelColorText";
import { useUpdateSupportTicketMutation } from "../../../../../newapi/warehouse/supportTicketApiSlice";
import {
    showToastMessageRequestError,
    showToastMessageUpdateRequest,
} from "../../../../../utils/toasts";

const ICONS = {
    [support_ticket_priority.LOW]:    KeyboardDoubleArrowDownIcon,
    [support_ticket_priority.MEDIUM]: DragHandleIcon,
    [support_ticket_priority.HIGH]:   KeyboardArrowUpIcon,
    [support_ticket_priority.URGENT]: KeyboardDoubleArrowUpIcon,
};

const PRIORITY_OPTIONS = [
    { id: support_ticket_priority.LOW,    name: support_ticket_priority.LOW },
    { id: support_ticket_priority.MEDIUM, name: support_ticket_priority.MEDIUM },
    { id: support_ticket_priority.HIGH,   name: support_ticket_priority.HIGH },
    { id: support_ticket_priority.URGENT, name: support_ticket_priority.URGENT },
];

const PriorityOption = (props) => {
    const { t } = useTranslation();
    const option = props.option ?? {};
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 1 }}>
            <PriorityBadge priority={option.id} size={20} />
            <Text semibold>{t(option.name)}</Text>
        </Box>
    );
};

const PriorityBadge = ({ priority, size = 32, sx, ticketId, disabled }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const [updateTicket] = useUpdateSupportTicketMutation();

    const level = priority || support_ticket_priority.MEDIUM;
    const Icon = ICONS[level] ?? ICONS[support_ticket_priority.MEDIUM];
    const { labelcolor } = getSupportTicketPriorityData(level, theme);

    const badge = (
        <Box
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: size >= 32 ? 4 : 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: colors[labelcolor][100],
                color: colors[labelcolor][400],
                ...sx,
            }}
        >
            <Icon sx={{ fontSize: Math.round(size * 0.6) }} />
        </Box>
    );

    const interactive = Boolean(ticketId) && !disabled;
    if (!interactive) return badge;

    const selectedIndex = PRIORITY_OPTIONS.findIndex((o) => o.id === level);

    const handleChange = async (idx) => {
        if (idx === -1) return;
        const next = PRIORITY_OPTIONS[idx]?.id;
        if (!next || next === priority) return;
        try {
            const res = await updateTicket({ id: ticketId, data: { priority: next } }).unwrap();
            showToastMessageUpdateRequest(t, res);
        } catch (e) {
            showToastMessageRequestError(t("update_failed"), t, e);
        }
    };

    return (
        <DropdownBaseV2
            displayName="name"
            options={PRIORITY_OPTIONS}
            selected={selectedIndex}
            onChange={handleChange}
            shComponent={<PriorityOption />}
            defaultStyling
            closeOnSelection
            disallowDeselection
            stopPropagation
            styles={{
                optionSx: {
                    paddingX: 1,
                    py: 0,
                    borderRadius: 3,
                    height: 32,
                },
            }}
        >
            {({ onClick }) => (
                <ButtonBase
                    onClick={onClick}
                    TouchRippleProps={{
                        style: { color: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[300] },
                    }}
                    sx={{ borderRadius: size >= 32 ? 4 : 3 }}
                >
                    {badge}
                </ButtonBase>
            )}
        </DropdownBaseV2>
    );
};

export default PriorityBadge;
